/**
 * Load Balance Board — design tokens.
 *
 * These come from the Wilya handoff (`wilya-tokens.css`) and are deliberately
 * kept separate from `planner-ui`: the board is a different visual system —
 * structural 1.5px ink borders, hairline dividers, no shadows, big Trebuchet
 * numerals — and mixing the two would blur both. Values here are the spec's,
 * not approximations.
 */
export const lbb = {
	// Ground
	desk: "#E9E7E4",
	paper: "#FFFFFF",
	alloy: "#F2F2F2",
	alloyWarm: "#F7F3EF",

	// Ink + structure
	ink: "#121826",
	navy: "#1A2442",
	navy40: "#8E94A8",
	navy20: "#C4C7D2",
	ink60: "#5B6380",
	hairline: "#E4E6EC",

	// Accent
	brick: "#C85E51",
	brickSoft: "#F1C5BE",
	brickDeep: "#A84838",
	/** Over-cap day cell wash */
	brickTint: "#FBEFEC",
	/** Row wash while a block is picked up */
	dropTint: "#FBF7F5",
	/** Neutral assignment block */
	blockFill: "#EDEFF4",

	// Semantic
	success: "#2E7D5B",
	/** Pale green wash for covered / drafted cells. */
	successTint: "#EAF1EC",
	warning: "#C9851F",

	// Structure
	borderInk: "1.5px solid #121826",
	borderHair: "1px solid #E4E6EC",

	// Type
	fontBrand: "'Trebuchet MS', 'Lucida Sans Unicode', 'Lucida Grande', system-ui, sans-serif",
	fontUi: "'Inter', 'Trebuchet MS', system-ui, sans-serif",
	fontMono: "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace",

	// Motion
	ease: "cubic-bezier(0.2, 0.8, 0.2, 1)",
} as const;

/** Shared eyebrow: 11px 700 caps, 0.14em. */
export const eyebrowSx = {
	fontFamily: lbb.fontUi,
	fontSize: 11,
	fontWeight: 700,
	letterSpacing: "0.14em",
	textTransform: "uppercase" as const,
};

/** The three-column spine every Day/Week row shares with its header. */
export const ROW_GRID = "300px 220px 1fr";

/**
 * LAST-RESORT per-day cap in hours, used only when no shift is known for the
 * day. The real cap is the worker's shift length (see derive.ts dayShiftCap /
 * weekShiftCap); week cap is the sum of their shifts, else this × 5.
 */
export const DEFAULT_CAP_HOURS = 8;

/** Fair band runs from cap − this, up to cap. */
export const FAIR_BAND_HOURS = 1.5;

export type Band = "over" | "fair" | "under"

export function bandFor(hours: number, cap: number, fairWidth = FAIR_BAND_HOURS): Band {
	if (hours > cap) {
		return "over";
	}
	return hours >= cap - fairWidth ? "fair" : "under";
}

export function bandColor(band: Band): string {
	if (band === "over") {
		return lbb.brick;
	}
	return band === "fair" ? lbb.navy : lbb.navy20;
}

/** "8h 09m" — the board writes every duration this way. */
export function fmtHours(hours: number): string {
	const total = Math.max(0, Math.round(hours * 60));
	return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, "0")}m`;
}

/** Compact form for dense cells: drops a zero minutes part. */
export function fmtHoursShort(hours: number): string {
	return fmtHours(hours).replace(" 00m", "");
}
